import { useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ScrollArea,
} from '@design-system';
import { Upload } from 'lucide-react';
import { toast } from 'sonner';
import {
  useImportWizardEvalRules,
  useWizardEvalRules,
} from '@/features/wizard-evals/hooks/use-wizard-evals';
import {
  diffRules,
  parseRulesCsv,
  type ParseRulesCsvResult,
  type RuleDiff,
} from '@/features/wizard-evals/utils/parseRulesCsv';

interface ImportRulesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ImportRulesDialog({ open, onOpenChange }: ImportRulesDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [parseResult, setParseResult] = useState<ParseRulesCsvResult | null>(null);

  const { data: existingRules } = useWizardEvalRules();
  const importMutation = useImportWizardEvalRules();

  const diff: RuleDiff | null = useMemo(() => {
    if (!parseResult || parseResult.rules.length === 0 || !existingRules) return null;
    return diffRules(parseResult.rules, existingRules);
  }, [parseResult, existingRules]);

  const reset = () => {
    setFileName(null);
    setParseResult(null);
    importMutation.reset();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  const handleFilePicked = async (file: File) => {
    const text = await file.text();
    const result = parseRulesCsv(text);
    setFileName(file.name);
    setParseResult(result);
  };

  const handleConfirm = async () => {
    if (!parseResult || parseResult.rules.length === 0) return;
    try {
      const result = await importMutation.mutateAsync(parseResult.rules);
      toast.success('Rules imported', {
        description: `Added ${result.added}, updated ${result.updated}, deactivated ${result.deactivated}.`,
      });
      handleClose(false);
    } catch (err: any) {
      toast.error('Import failed', {
        description: err?.message ?? 'Unknown error',
      });
    }
  };

  const canConfirm =
    !!parseResult &&
    parseResult.rules.length > 0 &&
    parseResult.errors.length === 0 &&
    !!diff;

  return (
    <Dialog
      open={open}
      onOpenChange={handleClose}
    >
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>Import rules from CSV</DialogTitle>
          <DialogDescription>
            Upsert each row. Any active rule whose carrier appears in the file but whose{' '}
            <code className="text-xs">ruleId</code> is missing will be marked inactive with an
            expiry timestamp. Other carriers are untouched.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) handleFilePicked(file);
              }}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="mr-1 h-4 w-4" />
              {fileName ? `Replace (${fileName})` : 'Choose CSV file'}
            </Button>
            <p className="mt-2 text-xs text-gray-600">
              Required columns: <code>ruleId</code> (or <code>rule_id</code>),{' '}
              <code>carrier</code>, <code>ruleText</code> (or <code>label</code>). Optional:{' '}
              <code>category</code>, <code>keywords</code> (pipe- or semicolon-delimited, or JSON
              array), <code>isActive</code>.
            </p>
          </div>

          {parseResult && parseResult.errors.length > 0 && (
            <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
              <p className="font-medium">Parse errors — fix these before importing:</p>
              <ul className="mt-1 list-disc pl-5">
                {parseResult.errors.slice(0, 10).map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
                {parseResult.errors.length > 10 && (
                  <li>…and {parseResult.errors.length - 10} more</li>
                )}
              </ul>
            </div>
          )}

          {diff && (
            <div className="flex flex-col gap-3 rounded border border-gray-200 p-3">
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <div className="rounded bg-green-50 p-2">
                  <div className="text-lg font-semibold text-green-700">{diff.adds.length}</div>
                  <div className="text-xs text-green-700">to add</div>
                </div>
                <div className="rounded bg-blue-50 p-2">
                  <div className="text-lg font-semibold text-blue-700">{diff.updates.length}</div>
                  <div className="text-xs text-blue-700">to update</div>
                </div>
                <div className="rounded bg-orange-50 p-2">
                  <div className="text-lg font-semibold text-orange-700">
                    {diff.deactivations.length}
                  </div>
                  <div className="text-xs text-orange-700">to deactivate</div>
                </div>
              </div>

              {diff.deactivations.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-gray-700">
                    Rules to deactivate ({diff.deactivations.length})
                  </summary>
                  <ScrollArea className="mt-1 max-h-40">
                    <ul className="space-y-0.5 pl-1">
                      {diff.deactivations.map(d => (
                        <li
                          key={d.ruleId}
                          className="font-mono text-[11px] text-gray-700"
                        >
                          <span className="text-orange-600">{d.ruleId}</span>{' '}
                          <span className="text-gray-600">— {d.ruleText}</span>
                        </li>
                      ))}
                    </ul>
                  </ScrollArea>
                </details>
              )}

              {diff.adds.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-gray-700">
                    Rules to add ({diff.adds.length})
                  </summary>
                  <ScrollArea className="mt-1 max-h-40">
                    <ul className="space-y-0.5 pl-1">
                      {diff.adds.map(d => (
                        <li
                          key={d.ruleId}
                          className="font-mono text-[11px] text-gray-700"
                        >
                          <span className="text-green-700">{d.ruleId}</span>{' '}
                          <span className="text-gray-600">— {d.ruleText}</span>
                        </li>
                      ))}
                    </ul>
                  </ScrollArea>
                </details>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm || importMutation.isPending}
          >
            {importMutation.isPending ? 'Importing…' : 'Confirm import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
